import React, { useState, useEffect } from "react";
import clsx from "clsx";
import {
  Box,
  Card,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  makeStyles,
  CardContent,
  TextField,
  InputAdornment,
  SvgIcon,
  FormLabel,
} from "@material-ui/core";
import TableContainer from "@material-ui/core/TableContainer";
import TableSortLabel from "@material-ui/core/TableSortLabel";
import { Search as SearchIcon } from "react-feather";
import UserAndDate from "src/components/common/UserAndDate";
import { kmLabels } from "src/constant/Texts";
import parse from "html-react-parser";
import withWidth, { isWidthUp } from "@material-ui/core/withWidth";
import Accordion from "@material-ui/core/Accordion";
import AccordionSummary from "@material-ui/core/AccordionSummary";
import AccordionDetails from "@material-ui/core/AccordionDetails";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import Typography from "@material-ui/core/Typography";
import { fillMissingTags } from "src/utils/utils";

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
  },
  paper: {
    width: "100%",
    marginBottom: theme.spacing(1),
  },
  table: {
    minWidth: 750,
    fontWeight: 501,
  },
  visuallyHidden: {
    border: 0,
    clip: "rect(0 0 0 0)",
    height: 1,
    margin: -1,
    overflow: "hidden",
    padding: 0,
    position: "absolute",
    top: 20,
    width: 1,
  },
  maincell: {
    paddingLeft: theme.spacing(1),
  },
  lpcol: {
    paddingLeft: theme.spacing(2),
  },
  selrow: {
    backgroundColor: theme.palette.common.tableHighlighted,
  },
}));

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy] || (!b[orderBy] && a[orderBy])) {
    return -1;
  }
  if (b[orderBy] > a[orderBy] || (b[orderBy] && !a[orderBy])) {
    return 1;
  }
  return 0;
}

function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

function EnhancedTableHead(props) {
  const {
    markable,
    headCells,
    classes,
    onSelectAllClick,
    order,
    orderBy,
    numSelected,
    rowCount,
    onRequestSort,
    setSortColumn,
  } = props;

  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>
        {markable ? (
          <TableCell component="td" padding="checkbox">
            <Checkbox
              width="3%"
              indeterminate={numSelected > 0 && numSelected < rowCount}
              checked={rowCount > 0 && numSelected === rowCount}
              onChange={onSelectAllClick}
              color="primary"
              inputProps={{ "aria-label": "Zaznacz wszystkie wiersze" }}
            />
          </TableCell>
        ) : (
          <></>
        )}
        {headCells.map((headCell) =>
          headCell.sortable ? (
            <TableCell
              width={headCell.width}
              key={headCell.id}
              align={headCell.numeric ? "right" : "left"}
              padding={headCell.disablePadding ? "none" : "normal"}
              sortDirection={orderBy === headCell.id ? order : false}
              className={
                !markable && headCell.main
                  ? classes.maincell
                  : headCell.lp
                  ? classes.lpcol
                  : ""
              }
            >
              <TableSortLabel
                active={orderBy === headCell.id}
                direction={orderBy === headCell.id ? order : "asc"}
                onClick={createSortHandler(headCell.id)}
              >
                {headCell.label}
                {orderBy === headCell.id ? (
                  <span className={classes.visuallyHidden}>
                    {order === "desc"
                      ? "sorted descending"
                      : "sorted ascending"}
                  </span>
                ) : null}
              </TableSortLabel>
            </TableCell>
          ) : (
            <TableCell
              width={headCell.width}
              component={headCell.label ? "th" : "td"}
              key={headCell.id}
              align={headCell.numeric ? "right" : "left"}
              padding={headCell.disablePadding ? "none" : "normal"}
              className={!markable && headCell.lp ? classes.lpcol : ""}
            >
              {headCell.label}
            </TableCell>
          )
        )}
      </TableRow>
    </TableHead>
  );
}

const KmFilteredTable = ({
  className,
  searchable = true,
  markable = true,
  headCells,
  rows,
  onSelectedRowIdsChange,
  possibleToCheckCount,
  defaultOrderBy,
  defaultOrder,
  defaultRowsPerPage,
  defaultPage,
  onSelectOrderBy,
  onSelectOrder,
  onSelectRowsPerPage,
  onSelectPage,
  mobHeadCells,
  title,
  highlightField,
  setSortColumn,
  ...rest
}) => {
  const classes = useStyles();
  const [order, setOrder] = React.useState(
    defaultOrder ? defaultOrder : "desc"
  );
  const [orderBy, setOrderBy] = React.useState(
    defaultOrderBy ? defaultOrderBy : "id"
  );
  const [rowsPerPage, setRowsPerPage] = React.useState(
    defaultRowsPerPage ? defaultRowsPerPage : 5
  );
  const [page, setPage] = React.useState(defaultPage ? defaultPage : 0);

  const [selected, setSelected] = React.useState([]);

  const [searchText, setSearchText] = useState("");
  const [data, setData] = useState([]);

  useEffect(() => {
    setData(rows);
    setTimeout(fillMissingTags(), 1000);
    return () => {};
  }, [rows]);

  // exclude column list from filter
  const excludeColumns = ["btns", "deleteSuccess", "id"];

  const setSelectedRowIds = (ids, idsToProceed) => {
    setSelected(ids);
    if (idsToProceed) {
      onSelectedRowIdsChange(idsToProceed);
    } else {
      onSelectedRowIdsChange(ids);
    }
  };

  function getComparator(order, orderBy) {
    if (setSortColumn) {
      setSortColumn(`${orderBy}_${order}`);
    }

    return order === "desc"
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
  }

  // handle change event of search input
  const handleChange = (value) => {
    setSearchText(value);
    filterData(value);
  };

  const filterData = (value) => {
    const lowercasedValue = value.toLowerCase().trim();
    if (lowercasedValue === "") setData(rows);
    else {
      const filteredData = rows.filter((item) => {
        return Object.keys(item).some((key) =>
          excludeColumns.includes(key)
            ? false
            : item[key]
            ? item[key]
                .toString()
                .toLowerCase()
                .includes(lowercasedValue)
            : false
        );
      });
      setData(filteredData);
    }
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
    if (onSelectOrder){
      onSelectOrder(isAsc ? "desc" : "asc");
    }
    if (onSelectOrderBy){
      onSelectOrderBy(property);
    }
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = data.map((n) => n.id);
      setSelectedRowIds(
        newSelecteds,
        data
          .filter(
            (n) => !n.relatedCount && n.relatedCount === 0 && !n.systemElement
          )
          .map((n) => n.id)
      );
      return;
    }
    setSelectedRowIds([]);
  };

  const handleClick = (event, name) => {
    const selectedIndex = selected.indexOf(name);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, name);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }

    setSelectedRowIds(newSelected);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    if (onSelectPage){
      onSelectPage(newPage);
    }
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    if (onSelectRowsPerPage){
      onSelectRowsPerPage(parseInt(event.target.value, 10));
    }
  };

  const getValueOrDefault = (value, dataType, dataLabels) => {
    if (dataType) {
      switch (dataType) {
        case 0:
          if (dataLabels && dataLabels.length === 2) {
            return value ? dataLabels[0] : dataLabels[1];
          } else {
            return value;
          }
        case 1:
          if (value) {
            return parse(`<ul><li>${value.join("</li><li>")}</li></ul>`);
          } else {
            return kmLabels.global.empty_value_indicator;
          }
        case 2:
          if (value) {
            return dataLabels && dataLabels[value]
              ? dataLabels[value]
              : "??nolabel??";
          } else {
            return kmLabels.global.empty_value_indicator;
          }

        case 3:
          return value && dataLabels?.length > 1
            ? dataLabels[1]
            : dataLabels[0];
        default:
          return value;
      }
    }

    return value ? value : kmLabels.global.empty_value_indicator;
  };

  const isSelected = (name) => selected.indexOf(name) !== -1;

  return (
    <div className={classes.root}>
      {searchable ? (
        <Box mt={1} mb={1}>
          <Card>
            <CardContent>
              <Box maxWidth={500}>
                <TextField
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SvgIcon fontSize="small" color="action">
                          <SearchIcon />
                        </SvgIcon>
                      </InputAdornment>
                    ),
                  }}
                  placeholder="Wyszukaj"
                  variant="standard"
                  value={searchText}
                  onChange={(e) => handleChange(e.target.value)}
                  inputProps={{ title: "Wyszukaj", "aria-label": "Wyszukaj" }}
                />
              </Box>
            </CardContent>
          </Card>
        </Box>
      ) : (
        <></>
      )}
      <Card className={clsx(classes.root, className)} {...rest}>
        {isWidthUp("sm", rest.width) || !mobHeadCells ? (
          <TableContainer>
            <Table
              className={classes.table}
              aria-label={title ? title : "Tabela"}
            >
              <EnhancedTableHead
                markable={markable}
                headCells={headCells}
                classes={classes}
                numSelected={selected.length}
                order={order}
                orderBy={orderBy}
                onSelectAllClick={handleSelectAllClick}
                onRequestSort={handleRequestSort}
                rowCount={data.length}
                setSortColumn={setSortColumn}
              />
              <TableBody>
                {stableSort(data, getComparator(order, orderBy))
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row, rowindex) => {
                    const isItemSelected = isSelected(row.id);
                    const labelId = `table-checkbox-${rowindex}`;

                    return (
                      <TableRow
                        hover
                        role="checkbox"
                        aria-checked={isItemSelected}
                        tabIndex={-1}
                        key={row.id}
                        selected={isItemSelected}
                        className={
                          highlightField && row[highlightField]
                            ? classes.selrow
                            : ""
                        }
                      >
                        {markable ? (
                          <TableCell padding="checkbox">
                            {(row.relatedCount && row.relatedCount > 0) ||
                            row.systemElement ? (
                              <></>
                            ) : (
                              <Checkbox
                                onClick={(event) => handleClick(event, row.id)}
                                checked={isItemSelected}
                                color="primary"
                                inputProps={{ "aria-labelledby": labelId }}
                              />
                            )}
                          </TableCell>
                        ) : (
                          <></>
                        )}

                        {headCells.map((cell, index) => {
                          if (cell.main) {
                            return (
                              <TableCell
                                key={`${index}${row.id}`}
                                id={labelId}
                                scope="row"
                                padding="none"
                                width={cell.width}
                                className={
                                  !markable
                                    ? classes.maincell
                                    : cell.dataType === 1
                                    ? classes.lpcol
                                    : ""
                                }
                              >
                                {getValueOrDefault(
                                  row[cell.fieldName],
                                  cell.dataType,
                                  cell.dataLabels
                                )}
                              </TableCell>
                            );
                          } else {
                            return cell.kmUdField ? (
                              <TableCell
                                key={`${index}${row.id}`}
                                align={cell.dataAlign}
                                width={cell.width}
                                className={
                                  cell.dataType === 1 ? classes.lpcol : ""
                                }
                              >
                                <UserAndDate
                                  username={getValueOrDefault(
                                    row[cell.fieldName],
                                    cell.dataType,
                                    cell.dataLabels
                                  )}
                                  date={getValueOrDefault(
                                    row[cell.fieldName2],
                                    cell.dataType,
                                    cell.dataLabels
                                  )}
                                  separator=", "
                                />
                              </TableCell>
                            ) : (
                              <TableCell
                                key={`${index}${row.id}`}
                                align={cell.dataAlign}
                                width={cell.width}
                                className={
                                  cell.lp || cell.dataType === 1
                                    ? classes.lpcol
                                    : ""
                                }
                              >
                                {cell.component
                                  ? cell.component(row, cell.customHandlers)
                                  : cell.lp
                                  ? rowindex + 1 + page * rowsPerPage
                                  : getValueOrDefault(
                                      row[cell.fieldName],
                                      cell.dataType,
                                      cell.dataLabels
                                    )}
                              </TableCell>
                            );
                          }
                        })}
                      </TableRow>
                    );
                  })}
                {/* {emptyRows > 0 && (
                <TableRow style={{ height: 53 * emptyRows }}>
                  <TableCell colSpan={headCells.length + 1} />
                </TableRow>
              )} */}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <div className={classes.root}>
            {stableSort(data, getComparator(order, orderBy))
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((row, rowindex) => {
                return (
                  <Accordion>
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-label="Rozwiń aby zobaczyć szczegóły"
                      aria-controls="additional-actions1-content"
                      id="additional-actions1-header"
                      className={
                        highlightField && row[highlightField]
                          ? classes.selrow
                          : ""
                      }
                    >
                      {mobHeadCells.map((cell, index) => {
                        if (cell.main) {
                          return (
                            <Box display="flex" alignItems="center">
                              <Typography>
                                {getValueOrDefault(
                                  row[cell.fieldName],
                                  cell.dataType,
                                  cell.dataLabels
                                )}
                              </Typography>
                            </Box>
                          );
                        } else if (cell.component) {
                          return cell.component(row, cell.customHandlers);
                        } else {
                          return <></>;
                        }
                      })}
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box
                        display="flex"
                        flexWrap="wrap"
                        alignContent="flex-start"
                        p={1}
                        m={1}
                      >
                        {headCells.map((cell, index) => {
                          if (!cell.main) {
                            return cell.kmUdField ? (
                              <Box p={2} key={`${index}${row.id}`}>
                                <FormLabel>{cell.label}</FormLabel>
                                <UserAndDate
                                  username={getValueOrDefault(
                                    row[cell.fieldName],
                                    cell.dataType,
                                    cell.dataLabels
                                  )}
                                  date={getValueOrDefault(
                                    row[cell.fieldName2],
                                    cell.dataType,
                                    cell.dataLabels
                                  )}
                                  separator=", "
                                />
                              </Box>
                            ) : (
                              <Box key={`${index}${row.id}`} p={2}>
                                <FormLabel>{cell.label}</FormLabel>
                                <Typography>
                                  {!cell.component && !cell.lp ? (
                                    getValueOrDefault(
                                      row[cell.fieldName],
                                      cell.dataType,
                                      cell.dataLabels
                                    )
                                  ) : (
                                    <></>
                                  )}
                                </Typography>
                              </Box>
                            );
                          } else {
                            return <></>;
                          }
                        })}
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                );
              })}
          </div>
        )}
        <TablePagination
          labelRowsPerPage="Wierszy:"
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={rows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Card>
    </div>
  );
};

export default withWidth()(KmFilteredTable);
